import { ApplicationService, ContactService, SchemaService } from '@amzn/awscat-portfolio-assessment-service-client';
import { Contact, DeserializedApplication } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Box, Button, ColumnLayout, Container, Grid, Header, Link,
    NonCancelableCustomEvent, Spinner, Toggle, ToggleProps } from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useHistory, useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { HelpPanelTopicOptions } from '../../common/Constants';
import { constants } from '../../common/Localization';
import { MatchParam } from '../../common/types';
import { HelpPanelContext } from '../../contexts/HelpPanelContext';
import PreviewBanner from '../common/PreviewBanner';

type Props = FlashContextInterface;
interface AttributeToLabel {
    [attribute: string]: string;
}

const appService = new ApplicationService();
const contactService = new ContactService();
const schemaService = new SchemaService();
const resourceName = 'applications';

function ApplicationDetailPage(props: Props) {
    const history = useHistory();
    const isMounted = useIsMounted();
    
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/management/application-detail/:applicationId',
    });
    const portfolioId = match?.params?.portfolioId || '';
    const applicationId = match?.params?.applicationId || '';
    function onContactEdit() {
        history.push(`/portfolio/${portfolioId}/management/contact-management`);
    }

    function onQuestionnaireClick() {
        history.push(`/portfolio/${portfolioId}/management/data-collection`);
    }
    const [ loading, setLoading ] = useState(true);
    const [ application, setApplication ] = useState<DeserializedApplication | undefined>(undefined);
    const [ contact, setContact ] = useState<Contact | undefined>(undefined);
    const [ attributesToDisplay, setAttributesToDisplay ] = useState<JSX.Element[] | null>([]);
    const { panelHandler } = useContext(HelpPanelContext);
    const [ hideNullAttributes, setHideNullAttributes ] = useState(true);
    const hideNullAttributesChange = (event: NonCancelableCustomEvent<ToggleProps.ChangeDetail>) => {
        const { checked } = event.detail;
        setHideNullAttributes(checked);
    };

    const [ inScope, setInScope ] = useState(true);
    const inScopeChange = async (event: NonCancelableCustomEvent<ToggleProps.ChangeDetail>) => {
        const { checked } = event.detail;
        setInScope(checked);
        try {
            await appService.updateInScope(applicationId, portfolioId, checked);
        } catch (err) {
            if (isMounted()) {
                props.flash.alert(FlashType.error, (err as Error).message, constants.CANNOT_UPDATE_IN_SCOPE);
                setInScope(!checked);
            }
        }
    };

    async function loadData() {
        setLoading(true);
        try {
            const app: DeserializedApplication = await appService.getApplication(applicationId, portfolioId);
            if (isMounted()) {
                setApplication(app);
            }
            if (app.businessOwnerId) {
                const appContact: Contact = await contactService.get(app.businessOwnerId, portfolioId);
                if (isMounted()) {
                    setContact(appContact);
                }
            }
            if (isMounted()) {
                setInScope(app.inScope);
            }
        } catch (e) {
            if (isMounted()) {
                props.flash.alert(FlashType.error,  (e as Error).message, constants.CANNOT_LOAD_APP_DETAIL);
                setApplication(undefined);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    }

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.APPLICATION_OVERVIEW);
    }, [ panelHandler ]);

    useEffect(() => {
        loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ props ]);

    const gridDefinition = [
        {colspan: {default: 12} },
        {colspan: {l: 6, m: 6, default: 12} },
        {colspan: {l: 6, m: 6, default: 12} },
        {colspan: {default: 12} },
    ];

    async function updateAttributes() {
        const attributesToExclude: Array<keyof DeserializedApplication> = [
            'description',
            'serverIds',
            'portfolioId',
            'businessOwnerId',
            'score',
            'ranking',
            'tags',
            'questionnaireId',
            'inScope',
            'createdAt',
            'updatedAt',
            'name',
        ];
        const filterAttributesToExclude = (applicationAttributes: string) =>
            !attributesToExclude.includes(applicationAttributes as keyof DeserializedApplication);
        try {
            const applicationHeaders: AttributeToLabel = {};
            const appSchemas = await schemaService.listAttributes(resourceName, portfolioId);
            if (!isMounted()) {
                return;
            }
            appSchemas.forEach(appSchema => {
                applicationHeaders[appSchema.value] = appSchema.text;
            });
            const attributeDisplay: JSX.Element[] | null = application ?
                (hideNullAttributes ?
                    Object.keys(application).filter((attribute: string) => !!application[attribute as keyof DeserializedApplication]) :
                    Object.keys(application)).filter(filterAttributesToExclude).map((attribute: string) => (
                    <div key={attribute}>
                        <div className='awsui-util-label'>{applicationHeaders[attribute]}</div>
                        <div>{application[attribute as keyof DeserializedApplication] ?? '-'}</div>
                    </div>
                )) : (null);
            setAttributesToDisplay(attributeDisplay);
        } catch (err) {
            if (isMounted()) {
                props.flash.alert(FlashType.error, (err as Error).message, constants.FAILED_TO_LOAD_SCHEMAS);
            }
        }
    }
    useEffect(() => {
        updateAttributes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ hideNullAttributes, application ]);

    const header = (
        <span>
            <Header
                variant='h1'
            >
                {application?.name ?? applicationId}
            </Header>
            <Toggle
                checked={inScope}
                onChange={inScopeChange}
            >
                {constants.IN_SCOPE_OF_PORTFOLIO}
            </Toggle>
        </span>
    );

    const descriptionHeader = (
        <Header
            variant='h2'
        >
            {constants.DESCRIPTION}
        </Header>
    );
    const description = (
        <Container
            header={descriptionHeader}
        >
            <p>{application?.description}</p>
        </Container>
    );

    const contactActions = (
        <Button
            onClick={onContactEdit}
        >
            {constants.EDIT_BUTTON_TEXT}
        </Button>
    );
    const contactHeader = (
        <Header
            variant='h2'
            actions={contactActions}
        >
            {constants.OWNER}
        </Header>
    );
    const contactDetails = contact ? (
        <Container
            header={contactHeader}
        >
            <ColumnLayout
                columns={2}
                variant='text-grid'
            >
                <div>
                    <div className='awsui-util-label'>{constants.NAME}</div>
                    <div>{contact.name}</div>
                </div>
                <div>
                    <div  className='awsui-util-label'>{constants.DEPARTMENT}</div>
                    <div>{application?.businessUnit}</div>
                </div>
                <div>
                    <div className='awsui-util-label'>{constants.EMAIL}</div>
                    <div>{contact.email}</div>
                </div>
                <div>
                    <div  className='awsui-util-label'>{constants.QUESTIONNAIRE}</div>
                    <div><Link onFollow={onQuestionnaireClick}>{constants.LINK}</Link></div>
                </div>
                <div>
                    <div className='awsui-util-label'>{constants.PHONE}</div>
                    <div>{contact.phone}</div>
                </div>
            </ColumnLayout>
        </Container>
    ) : (
        <Container>
            <Box textAlign='center'>
                {constants.NO_OWNER_ASSIGNED_TO_APP}<br/><br/>
                <Button
                    iconName='add-plus'
                    onClick={onContactEdit}
                >
                    {constants.ADD_OWNER}
                </Button>
            </Box>
        </Container>
    );

    const attributesHeaderActions = (
        <Toggle
            checked={hideNullAttributes}
            onChange={hideNullAttributesChange}
        >
            {constants.SHOW_ONLY_ATTRIBUTES_WITH_VALUES}
        </Toggle>
    );

    const attributesHeader = (
        <Header
            variant='h2'
            actions={attributesHeaderActions}
        >
            {constants.ATTRIBUTES}
        </Header>
    );

    const attributes = (
        <Container
            header={attributesHeader}
        >
            <ColumnLayout
                columns={4}
                variant='text-grid'
            >
                {attributesToDisplay}
            </ColumnLayout>
        </Container>
    );

    const content = !loading && (
        <Grid data-testid='content'
            gridDefinition={gridDefinition}
        >
            {header}
            {description}
            {contactDetails}
            {attributes}
        </Grid>
    );

    return (
        <div className='awsui'>
            <PreviewBanner />
            {loading ? <Spinner size='big' /> : null}
            {content}
        </div>
    );
}

export default withFlashContext(ApplicationDetailPage);
